import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { keys } from '../../lib/queryClient';
import { useAuth } from '../auth/AuthProvider';
import {
  DEFAULT_TIMEZONE,
  type ClientDoc,
  type CustomRoleDoc,
  type MemberDoc,
  type OrgDoc,
  type ProjectDoc,
  type Role,
  type WorkTarget,
  type Plan,
  type PlanFeature,
  type Module,
  FEATURE_MODULE,
  cleanModules,
  effectivePlan,
  pathVisible,
  personModules,
  planAllows,
  workspaceModules,
} from '../../types';
import { cleanCapabilities } from '../../shared/capabilities';

/** The org document, its people, its clients and its projects. */

export function useOrg(orgId: string | null) {
  return useQuery({
    queryKey: keys.org(orgId ?? 'none'),
    enabled: !!orgId,
    // Branding and settings change rarely and are read on every screen.
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<OrgDoc> => {
      const snap = await getDoc(doc(db, 'organizations', orgId!));
      if (!snap.exists()) throw new Error('not-found');
      return snap.data() as OrgDoc;
    },
  });
}

/**
 * Every org the signed-in person belongs to.
 *
 * The claim carries the ids, so this is one `get` per org and no collection
 * query — `list` on /organizations is refused to everyone but the platform
 * owner. It is what resolves the `/:orgSlug` segment in the URL to an org,
 * and what fills the switcher.
 */
export function useMyOrgs(orgIds: string[]) {
  const sorted = [...orgIds].sort();
  return useQuery({
    queryKey: ['orgs', sorted],
    enabled: sorted.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<OrgDoc[]> => {
      const snaps = await Promise.all(
        sorted.map((orgId) => getDoc(doc(db, 'organizations', orgId))),
      );
      return snaps
        .filter((snap) => snap.exists())
        .map((snap) => snap.data() as OrgDoc)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}

export function useMembers(orgId: string | null) {
  return useQuery({
    queryKey: keys.members(orgId ?? 'none'),
    enabled: !!orgId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<MemberDoc[]> => {
      const snap = await getDocs(collection(db, 'organizations', orgId!, 'members'));
      return snap.docs
        .map((d) => d.data() as MemberDoc)
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
    },
  });
}

/** Names by uid, for rendering history that outlives the person. */
export function useMemberLookup(orgId: string | null) {
  const { data } = useMembers(orgId);
  const lookup = new Map<string, MemberDoc>();
  for (const member of data ?? []) lookup.set(member.uid, member);
  return lookup;
}

export function useClients(orgId: string | null, includeArchived = false) {
  return useQuery({
    queryKey: [...keys.clients(orgId ?? 'none'), includeArchived],
    enabled: !!orgId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<ClientDoc[]> => {
      const snap = await getDocs(collection(db, 'organizations', orgId!, 'clients'));
      return snap.docs
        .map((d) => d.data() as ClientDoc)
        .filter((c) => includeArchived || !c.archived)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}

export function useProjects(orgId: string | null, includeArchived = false) {
  return useQuery({
    queryKey: [...keys.projects(orgId ?? 'none'), includeArchived],
    enabled: !!orgId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<ProjectDoc[]> => {
      const base = collection(db, 'organizations', orgId!, 'projects');
      const snap = await getDocs(
        includeArchived ? base : query(base, where('status', '==', 'active')),
      );
      return snap.docs
        .map((d) => d.data() as ProjectDoc)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}

export function useProject(orgId: string | null, projectId: string | undefined) {
  return useQuery({
    queryKey: keys.project(orgId ?? 'none', projectId ?? 'none'),
    enabled: !!orgId && !!projectId,
    queryFn: async (): Promise<ProjectDoc> => {
      const snap = await getDoc(doc(db, 'organizations', orgId!, 'projects', projectId!));
      if (!snap.exists()) throw new Error('not-found');
      return snap.data() as ProjectDoc;
    },
  });
}

/** Branding and settings. Only the fields the rules allow an admin to touch. */
export function useUpdateOrgSettings(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<OrgDoc>) => {
      await updateDoc(doc(db, 'organizations', orgId), { ...patch, orgId });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.org(orgId) }),
  });
}

/**
 * Clients and projects are written straight from the client.
 *
 * Both are manager-up in the rules and carry no derived figures, so there is
 * nothing here a callable would be protecting. A project's `clientId` is
 * denormalised onto every task created under it, which is why changing it
 * later only affects new tasks.
 */

export interface NewClient {
  name: string;
  color: string;
}

export function useCreateClient(orgId: string, actor: { uid: string; name: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewClient): Promise<ClientDoc> => {
      const ref = doc(collection(db, 'organizations', orgId, 'clients'));
      const client: ClientDoc = {
        orgId,
        clientId: ref.id,
        name: input.name.trim(),
        color: input.color,
        archived: false,
        createdBy: actor.uid,
        createdByName: actor.name,
        createdAt: Timestamp.now(),
      };
      await setDoc(ref, client);
      return client;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.clients(orgId) }),
  });
}

export type ClientPatch = Partial<Pick<ClientDoc, 'name' | 'color' | 'archived' | 'retainer'>>;

export function useUpdateClient(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientId, patch }: { clientId: string; patch: ClientPatch }) => {
      // orgId travels with every write: the rules reject a body whose orgId
      // disagrees with the path.
      await updateDoc(doc(db, 'organizations', orgId, 'clients', clientId), { ...patch, orgId });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.clients(orgId) }),
  });
}

export interface NewProject {
  name: string;
  description: string;
  clientId: string | null;
  color: string;
  budgetMinutes: number | null;
}

export function useCreateProject(orgId: string, actor: { uid: string; name: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewProject): Promise<ProjectDoc> => {
      const ref = doc(collection(db, 'organizations', orgId, 'projects'));
      const project: ProjectDoc = {
        orgId,
        projectId: ref.id,
        name: input.name.trim(),
        description: input.description.trim(),
        clientId: input.clientId,
        color: input.color,
        status: 'active',
        budgetMinutes: input.budgetMinutes,
        trackedMinutesTotal: 0,
        createdBy: actor.uid,
        createdByName: actor.name,
        createdAt: Timestamp.now(),
      };
      await setDoc(ref, project);
      return project;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.projects(orgId) }),
  });
}

export type ProjectPatch = Partial<
  Pick<
    ProjectDoc,
    | 'name'
    | 'description'
    | 'clientId'
    | 'color'
    | 'status'
    | 'budgetMinutes'
    | 'health'
    | 'healthNote'
    | 'healthUpdatedAt'
    | 'healthUpdatedByName'
    | 'automations'
    | 'brief'
    | 'briefUpdatedAt'
    | 'briefUpdatedByName'
  >
>;

export function useUpdateProject(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, patch }: { projectId: string; patch: ProjectPatch }) => {
      await updateDoc(doc(db, 'organizations', orgId, 'projects', projectId), { ...patch, orgId });
    },
    onSuccess: (_result, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: keys.projects(orgId) });
      queryClient.invalidateQueries({ queryKey: keys.project(orgId, projectId) });
    },
  });
}

/**
 * The organisation's timezone, which is the only timezone in the product.
 *
 * Every date the client derives — today, this week, whether a task is overdue —
 * has to come from this rather than from the browser, or it disagrees with the
 * `dateKey` the server wrote. Falls back to the default while the org document
 * is still loading, which is the same value a new org is created with.
 */
/**
 * What the workspace's plan allows. Read from the org document the screen
 * already has; the functions enforce the same table on the server.
 */
export function usePlan(orgId: string | null): { plan: Plan; allows: (feature: PlanFeature) => boolean } {
  const { data: org } = useOrg(orgId);
  const modules = useModules(orgId);
  const plan = org ? effectivePlan(org) : 'free';
  // A feature shows when the plan includes it and its module is on for this
  // person. Folding the second question in here is what makes switching a
  // module off reach every control the plan already gates, in one place.
  return {
    plan,
    allows: (feature) => {
      if (!planAllows(plan, feature)) return false;
      const module = FEATURE_MODULE[feature];
      return module === null || modules.has(module);
    },
  };
}

export interface ModuleAccess {
  /** The workspace's own switches, before anyone is narrowed. */
  workspace: Module[];
  /** What the signed-in person sees. */
  mine: Module[];
  has: (module: Module) => boolean;
  /** Whether an org-relative path is one this person can open. */
  canOpen: (path: string) => boolean;
  /**
   * Both documents have loaded. Until then `mine` is the workspace's list,
   * so a gate that sends people away waits for this rather than bouncing
   * someone whose membership simply has not arrived.
   */
  ready: boolean;
}

/**
 * What the signed-in person sees in this workspace: the workspace's modules,
 * narrowed by their own membership when an admin chose so. Read from the org
 * and members queries every screen already has cached; it costs no read of
 * its own. Visibility only — see src/shared/modules.ts.
 */
export function useModules(orgId: string | null): ModuleAccess {
  const { user, role, claims, isManagerUp } = useAuth();
  const { data: org } = useOrg(orgId);
  const { data: members } = useMembers(orgId);
  const me = members?.find((m) => m.uid === user?.uid);
  const ready = !!org && !!members;
  const workspace = workspaceModules(org);
  const cacheKey = `trackilero.modules.${orgId ?? ''}.${user?.uid ?? ''}`;
  // Until both documents arrive, what this person saw last time — or, on a
  // first visit, only the pages that are always there. Starting from the
  // workspace's list instead showed a narrowed person the areas they are not
  // meant to see for the moment it took their membership to load.
  const mine = ready ? personModules(org, me ?? (role ? { role } : null)) : readCachedModules(cacheKey);
  useEffect(() => {
    if (!ready) return;
    try {
      localStorage.setItem(cacheKey, JSON.stringify(mine));
    } catch {
      // A per-browser convenience; without it the first paint is just plainer.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, cacheKey, mine.join()]);
  const set = new Set(mine);
  // Setting up the work is the manager's job in every setup; a custom role
  // holding work.manage counts the same.
  const managesWork = isManagerUp || (role === 'custom' && !!orgId && (claims.caps?.[orgId] ?? []).includes('wm'));
  return {
    workspace,
    mine,
    has: (module) => set.has(module),
    canOpen: (path) => pathVisible(path, mine, managesWork),
    ready,
  };
}

function readCachedModules(key: string): Module[] {
  try {
    const raw = localStorage.getItem(key);
    return raw ? cleanModules(JSON.parse(raw) as unknown[]) : [];
  } catch {
    return [];
  }
}

/**
 * The workspace's switches, written straight to the org document: an admin
 * field the rules list, granting nothing. Optimistic, because a switch that
 * waits for a round trip feels broken; a refusal puts the old list back.
 */
export function useSetWorkspaceModules(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (modules: Module[]) => {
      await updateDoc(doc(db, 'organizations', orgId), { orgId, modules });
    },
    onMutate: async (modules) => {
      await queryClient.cancelQueries({ queryKey: keys.org(orgId) });
      const previous = queryClient.getQueryData<OrgDoc>(keys.org(orgId));
      if (previous) queryClient.setQueryData<OrgDoc>(keys.org(orgId), { ...previous, modules });
      return { previous };
    },
    onError: (_error, _modules, context) => {
      if (context?.previous) queryClient.setQueryData(keys.org(orgId), context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: keys.org(orgId) }),
  });
}

/**
 * One person's view, narrowed or opened back up. Null follows the
 * workspace. Optimistic for the same reason as the workspace switches: this
 * is the one-click control in the People list.
 */
export function useSetMemberModules(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ uid, modules }: { uid: string; modules: Module[] | null }) => {
      await updateDoc(doc(db, 'organizations', orgId, 'members', uid), { orgId, modules });
    },
    onMutate: async ({ uid, modules }) => {
      await queryClient.cancelQueries({ queryKey: keys.members(orgId) });
      const previous = queryClient.getQueryData<MemberDoc[]>(keys.members(orgId));
      if (previous) {
        queryClient.setQueryData<MemberDoc[]>(
          keys.members(orgId),
          previous.map((m) => (m.uid === uid ? { ...m, modules } : m)),
        );
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(keys.members(orgId), context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: keys.members(orgId) }),
  });
}

export function useOrgTimezone(orgId: string | null): string {
  const { data: org } = useOrg(orgId);
  return org?.timezone || DEFAULT_TIMEZONE;
}

/**
 * Your own name, as everyone else sees it.
 *
 * Written to two places on purpose. `/users/{uid}` is the account and drives
 * the greeting and the avatar; the membership is what every task, entry and
 * leave record was stamped with, and what the People list shows. Nothing syncs
 * them, so a write that touched only one would leave the interface disagreeing
 * with itself about who you are.
 *
 * Historical records keep the name they were written with, which is the point
 * of storing it on them — renaming yourself does not rewrite last month.
 */
/**
 * One person's hours, days and availability, written by themselves or by an
 * admin. The rules allow exactly those two writers and nobody else.
 */
export function useUpdateWorkTarget(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ uid, workTarget }: { uid: string; workTarget: WorkTarget }) => {
      await updateDoc(doc(db, 'organizations', orgId, 'members', uid), { orgId, workTarget });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.members(orgId) }),
  });
}

/**
 * An admin editing somebody else's membership. Role is left out when it is
 * unchanged so the rules' "cannot edit your own role" check never trips on a
 * no-op.
 */
export function useUpdateMember(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      uid,
      ...patch
    }: {
      uid: string;
      displayName?: string;
      jobTitle?: string | null;
      role?: Role;
      customRoleId?: string | null;
      approverUid?: string | null;
      workTarget?: WorkTarget;
      modules?: Module[] | null;
    }) => {
      const clean: Record<string, unknown> = { orgId };
      if (patch.modules !== undefined) clean.modules = patch.modules;
      if (patch.displayName !== undefined) clean.displayName = patch.displayName.trim();
      if (patch.jobTitle !== undefined) clean.jobTitle = patch.jobTitle?.trim() || null;
      if (patch.role !== undefined) clean.role = patch.role;
      // Always written alongside the role, never on its own: 'custom' with no
      // id holds nothing, and an id left behind on a built-in role is a
      // reference the claim writer would have to ignore.
      if (patch.customRoleId !== undefined) clean.customRoleId = patch.customRoleId;
      if (patch.approverUid !== undefined) clean.approverUid = patch.approverUid;
      if (patch.workTarget !== undefined) clean.workTarget = patch.workTarget;
      await updateDoc(doc(db, 'organizations', orgId, 'members', uid), clean);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.members(orgId) }),
  });
}

export function useUpdateMyProfile(orgId: string, uid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: {
      displayName: string;
      jobTitle: string | null;
      workTarget: WorkTarget;
      photoUrl: string | null;
    }) => {
      const displayName = patch.displayName.trim();
      await Promise.all([
        updateDoc(doc(db, 'users', uid), { displayName, photoUrl: patch.photoUrl }),
        updateDoc(doc(db, 'organizations', orgId, 'members', uid), {
          orgId,
          displayName,
          photoUrl: patch.photoUrl,
          jobTitle: patch.jobTitle?.trim() || null,
          workTarget: patch.workTarget,
        }),
      ]);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.members(orgId) }),
  });
}

// ---------- composed roles ----------

/**
 * The roles this workspace composed.
 *
 * Read by everybody, because a role's name renders beside the people who hold
 * it. Written by admins only, which the rules enforce rather than this file.
 */
export function useCustomRoles(orgId: string | null) {
  return useQuery({
    queryKey: ['org', orgId ?? 'none', 'roles'],
    enabled: !!orgId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<CustomRoleDoc[]> => {
      const snap = await getDocs(collection(db, 'organizations', orgId!, 'roles'));
      return snap.docs
        .map((d) => d.data() as CustomRoleDoc)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}

/**
 * How many people hold each role.
 *
 * A count query rather than a counter on the document: a counter would need
 * maintaining on every membership write and a story for when it drifts, for a
 * number one screen shows once. `getCountFromServer` bills a fraction of a
 * read and returns no documents.
 */
export function useRoleHolderCounts(orgId: string | null, roleIds: readonly string[]) {
  const ids = [...roleIds].sort().join(',');
  return useQuery({
    queryKey: ['org', orgId ?? 'none', 'roleHolders', ids],
    enabled: !!orgId && roleIds.length > 0,
    staleTime: 60_000,
    queryFn: async (): Promise<Record<string, number>> => {
      const entries = await Promise.all(
        roleIds.map(async (roleId) => {
          const snap = await getCountFromServer(
            query(
              collection(db, 'organizations', orgId!, 'members'),
              where('customRoleId', '==', roleId),
              where('status', '==', 'active'),
            ),
          );
          return [roleId, snap.data().count] as const;
        }),
      );
      return Object.fromEntries(entries);
    },
  });
}

export function useSaveCustomRole(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      roleId: string;
      name: string;
      description: string;
      capabilities: string[];
      basedOn: string | null;
      createdBy: string;
      createdByName: string;
      isNew: boolean;
    }) => {
      const ref = doc(db, 'organizations', orgId, 'roles', input.roleId);
      const body = {
        orgId,
        roleId: input.roleId,
        name: input.name.trim(),
        description: input.description.trim(),
        // Cleaned here as well as in the claim writer: an unknown code is a
        // permission nobody can reason about, and a capability without its
        // dependency is a role that looks like it works.
        capabilities: cleanCapabilities(input.capabilities),
        basedOn: input.basedOn,
        updatedAt: Timestamp.now(),
        ...(input.isNew
          ? { createdBy: input.createdBy, createdByName: input.createdByName, createdAt: Timestamp.now() }
          : {}),
      };
      await setDoc(ref, body, { merge: !input.isNew });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'roles'] });
    },
  });
}

export function useDeleteCustomRole(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) => deleteDoc(doc(db, 'organizations', orgId, 'roles', roleId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'roles'] });
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'members'] });
    },
  });
}

/**
 * What to print where a role is shown.
 *
 * A composed role's name is the role, as far as anybody reading a list is
 * concerned — "HR", not "Custom role". Falls back to the generic label while
 * the roles are still loading, or if the member points at a role that has been
 * deleted, which is also exactly what that person can now do.
 */
export function useRoleLabel(orgId: string | null) {
  const roles = useCustomRoles(orgId);
  return (member: Pick<MemberDoc, 'role' | 'customRoleId'>, fallback: string): string => {
    if (member.role !== 'custom') return fallback;
    const found = (roles.data ?? []).find((r) => r.roleId === member.customRoleId);
    return found?.name ?? fallback;
  };
}
